import {
	json,
	type LoaderFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
} from '@remix-run/node'
import {
	Form,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useMatches,
	useSubmit,
} from '@remix-run/react'
import { withSentry } from '@sentry/remix'
import { useRef, useEffect, type SVGProps } from 'react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { AudioPlayer } from './components/audio-player.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { SearchBar } from './components/search-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from './components/ui/dropdown-menu.tsx'
import { Icon, href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import {
	useOptionalTheme,
	useTheme,
} from './routes/resources+/theme-switch.tsx'
import fontStyleSheetUrl from './styles/font.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getUserImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser, useUser } from './utils/user.ts'

export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'preload', href: fontStyleSheetUrl, as: 'style' },
		{ rel: 'stylesheet', href: fontStyleSheetUrl },
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{ title: data ? 'Otemba Sake' : 'Error | Otemba Sake' },
		{ name: 'description', content: `Your own captain's log` },
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { id: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = honeypot.getInputProps()

	return json(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const theme = useTheme()
	const matches = useMatches()
	const isOnSearchPage = matches.find((m) => m.id === 'routes/users+/index')
	const searchBar = isOnSearchPage ? null : <SearchBar status="idle" />
	useToast(data.toast)
	const audioRef = useRef<HTMLAudioElement>(null)

	useEffect(() => {
		const playAudio = async () => {
			try {
				if (audioRef.current) {
					await audioRef.current.play()
				}
			} catch (error) {
				console.log('Autoplay prevented:', error)
				// You could show a "Click to play" button/state here
			}
		}
		void playAudio()
	}, [])

	const navigation = {
		discover: [
			{ name: 'Quiz: What Sake am I?', href: '#' },
			{ name: 'The Regions', href: '#' },
			{ name: 'Sake Sommelier', href: '#' },
			{ name: 'About Sake', href: '#' },
			{ name: 'Store', href: '#' },
		],
		breweries: [
			{ name: 'Akita Meijo Shuzo', href: '/about' },
			{ name: 'Aoki Shuzo', href: '/about' },
			{ name: 'Aramasa Shuzo', href: '/about' },
			{ name: 'Fujii Shuzo', href: '/about' },
			{ name: 'Gensaka Shuzo', href: '/about' },
			{ name: 'Hakuto Shuzo', href: '/about' },
			{ name: 'View all...', href: '/about' },
		],
		company: [
			{ name: 'About', href: '#' },
			{ name: 'Blog', href: '#' },
			{ name: 'Jobs', href: '#' },
			{ name: 'Press', href: '#' },
		],
		legal: [
			{ name: 'Terms of service', href: '#' },
			{ name: 'Privacy policy', href: '#' },
			{ name: 'License', href: '#' },
		],
		social: [
			{
				name: 'Facebook',
				href: '#',
				icon: (props: JSX.IntrinsicAttributes & SVGProps<SVGSVGElement>) => (
					<svg fill="currentColor" viewBox="0 0 24 24" {...props}>
						<path
							fillRule="evenodd"
							d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
							clipRule="evenodd"
						/>
					</svg>
				),
			},
			{
				name: 'Instagram',
				href: '#',
				icon: (props: JSX.IntrinsicAttributes & SVGProps<SVGSVGElement>) => (
					<svg fill="currentColor" viewBox="0 0 24 24" {...props}>
						<path
							fillRule="evenodd"
							d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
							clipRule="evenodd"
						/>
					</svg>
				),
			},
			{
				name: 'X',
				href: '#',
				icon: (props: JSX.IntrinsicAttributes & SVGProps<SVGSVGElement>) => (
					<svg fill="currentColor" viewBox="0 0 24 24" {...props}>
						<path d="M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1742 10.5689L12.8709 11.5655L18.6861 19.8835H16.2995L11.5541 13.096V13.0956Z" />
					</svg>
				),
			},
			{
				name: 'GitHub',
				href: '#',
				icon: (props: JSX.IntrinsicAttributes & SVGProps<SVGSVGElement>) => (
					<svg fill="currentColor" viewBox="0 0 24 24" {...props}>
						<path
							fillRule="evenodd"
							d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
							clipRule="evenodd"
						/>
					</svg>
				),
			},
			{
				name: 'YouTube',
				href: '#',
				icon: (props: JSX.IntrinsicAttributes & SVGProps<SVGSVGElement>) => (
					<svg fill="currentColor" viewBox="0 0 24 24" {...props}>
						<path
							fillRule="evenodd"
							d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z"
							clipRule="evenodd"
						/>
					</svg>
				),
			},
		],
	}

	return (
		<>
			<div className="flex h-screen flex-col justify-between">
				<header className="container py-6">
					<nav className="animate-slide-bottom[animation-delay:5s] delay-5000 flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
						<Logo variant="light" />

						<div
							onClick={async () => {
								try {
									if (audioRef.current?.paused) {
										await audioRef.current.play()
									} else {
										audioRef.current?.pause()
									}
								} catch (error) {
									console.log('Playback failed:', error)
								}
							}}
						>
							<AudioPlayer />
						</div>
						<audio
							ref={audioRef}
							id="backgroundAudio"
							src={`/audio/audio1.mp3`}
							className="hidden"
						/>

						<div className="flex items-center gap-10">
							{user ? (
								<UserDropdown />
							) : (
								<Button asChild variant="default" size="lg">
									<Link to="/login">My Account</Link>
								</Button>
							)}
						</div>
					</nav>
				</header>

				<div className="flex-1">
					<Outlet />
				</div>

				<footer className="relative flex bg-gray-900">
					<div className="mx-auto max-w-7xl px-6 pb-8 pt-16 sm:pt-24 lg:px-8 lg:pt-32">
						<div className="xl:grid xl:grid-cols-3 xl:gap-8">
							<Logo variant="dark" />
							<div className="mt-16 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0">
								<div className="md:grid md:grid-cols-2 md:gap-8">
									<div>
										<h3 className="font-semibold text-sm/6 text-white">
											Discover
										</h3>
										<ul role="list" className="mt-6 space-y-4">
											{navigation.discover.map((item) => (
												<li key={item.name}>
													<a
														href={item.href}
														className="text-sm/6 text-gray-400 hover:text-white"
													>
														{item.name}
													</a>
												</li>
											))}
										</ul>
									</div>
									<div className="mt-10 md:mt-0">
										<h3 className="font-semibold text-sm/6 text-white">
											Breweries
										</h3>
										<ul role="list" className="mt-6 space-y-4">
											{navigation.breweries.map((item) => (
												<li key={item.name}>
													<a
														href={item.href}
														className="text-sm/6 text-gray-400 hover:text-white"
													>
														{item.name}
													</a>
												</li>
											))}
										</ul>
									</div>
								</div>
								<div className="md:grid md:grid-cols-2 md:gap-8">
									<div>
										<h3 className="font-semibold text-sm/6 text-white">
											Company
										</h3>
										<ul role="list" className="mt-6 space-y-4">
											{navigation.company.map((item) => (
												<li key={item.name}>
													<a
														href={item.href}
														className="text-sm/6 text-gray-400 hover:text-white"
													>
														{item.name}
													</a>
												</li>
											))}
										</ul>
									</div>
									<div className="mt-10 md:mt-0">
										<h3 className="font-semibold text-sm/6 text-white">
											Legal
										</h3>
										<ul role="list" className="mt-6 space-y-4">
											{navigation.legal.map((item) => (
												<li key={item.name}>
													<a
														href={item.href}
														className="text-sm/6 text-gray-400 hover:text-white"
													>
														{item.name}
													</a>
												</li>
											))}
										</ul>
									</div>
								</div>
							</div>
						</div>
						<div className="mt-16 border-t border-white/10 pt-8 sm:mt-20 lg:mt-24 lg:flex lg:items-center lg:justify-between">
							<div>
								<h3 className="font-semibold text-sm/6 text-white">
									Subscribe to our newsletter
								</h3>
								<p className="mt-2 text-sm/6 text-gray-300">
									The latest Sake news and notifications about fresh shipments,
									straight to your inbox.
								</p>
							</div>
							<form className="mt-6 sm:flex sm:max-w-md lg:mt-0">
								<label htmlFor="email-address" className="sr-only">
									Email address
								</label>
								<input
									id="email-address"
									name="email-address"
									type="email"
									required
									placeholder="Enter your email"
									autoComplete="email"
									className="w-full min-w-0 appearance-none rounded-md border-0 bg-white/5 px-3 py-1.5 text-base text-white shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:w-56 sm:text-sm/6"
								/>
								<div className="mt-4 sm:ml-4 sm:mt-0 sm:shrink-0">
									<button
										type="submit"
										className="flex w-full items-center justify-center rounded-md bg-indigo-500 px-3 py-2 font-semibold text-sm text-white shadow-sm hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
									>
										Subscribe
									</button>
								</div>
							</form>
						</div>
						<div className="mt-8 border-t border-white/10 pt-8 md:flex md:items-center md:justify-between">
							<div className="flex gap-x-6 md:order-2">
								{navigation.social.map((item) => (
									<a
										key={item.name}
										href={item.href}
										className="text-gray-400 hover:text-gray-300"
									>
										<span className="sr-only">{item.name}</span>
										<item.icon aria-hidden="true" className="size-6" />
									</a>
								))}
							</div>
							<p className="mt-8 text-sm/6 text-gray-400 md:order-1 md:mt-0">
								&copy; 2024 Otemba Sake Amsterdam
							</p>
						</div>
					</div>
				</footer>
			</div>

			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</>
	)
}

function Logo({ variant }: { variant?: 'dark' | 'light' }) {
	const theme = useTheme()
	const logoSrc =
		variant === 'dark'
			? '/img/logo-otemba-darkbg.svg'
			: theme === 'dark'
				? '/img/logo-otemba.svg'
				: '/img/logo-otemba.svg'

	return (
		<Link to="/" className="group grid leading-snug">
			<img
				src={logoSrc}
				alt="Otemba Sake"
				className="h-14 w-auto group-hover:opacity-80"
			/>
			<span className="sr-only">Otemba Sake</span>
		</Link>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default withSentry(AppWithProviders)

function UserDropdown() {
	const user = useUser()
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button asChild variant="secondary">
					<Link
						to={`/users/${user.username}`}
						// this is for progressive enhancement
						onClick={(e) => e.preventDefault()}
						className="flex items-center gap-2"
					>
						<img
							className="h-8 w-8 rounded-full object-cover"
							alt={user.name ?? user.username}
							src={getUserImgSrc(user.image?.id)}
						/>
						<span className="text-body-sm font-bold">
							{user.name ?? user.username}
						</span>
					</Link>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/${user.username}`}>
							<Icon className="text-body-md" name="avatar">
								Profile
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/${user.username}/notes`}>
							<Icon className="text-body-md" name="pencil-2">
								Notes
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem
						asChild
						// this prevents the menu from closing before the form submission is completed
						onSelect={(event) => {
							event.preventDefault()
							submit(formRef.current)
						}}
					>
						<Form action="/logout" method="POST" ref={formRef}>
							<Icon className="text-body-md" name="exit">
								<button type="submit">Logout</button>
							</Icon>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
